import React from 'react';
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Menu,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedMenuItem as MenuItem } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';
import {
  AccountCircle,
  AdminPanelSettings,
  Apple,
  AccessTime,
  Calculate,
  Class,
  Logout,
  Menu as MenuIcon,
  MenuBook,
  PeopleAlt,
  Settings as SettingsIcon,
  ShowChart,
  Storage,
  WbSunny
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { translateInline } from '../../i18n/inlineTranslations';
import LanguageSwitcher from '../i18n/LanguageSwitcher';
import { BRAND } from '../../config/brand';
import { translatePublicSite } from '../../i18n/publicSiteText';

export const WEBSITE_NAVBAR_OFFSET = {
  xs: 'var(--kundlistar-navbar-height, 64px)',
  md: 'var(--kundlistar-navbar-height, 72px)'
};

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCollapsedNav = useMediaQuery(theme.breakpoints.down('lg'));
  const { state, logout } = useAuth();
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const [menuEl, setMenuEl] = React.useState<HTMLElement | null>(null);
  const [accountEl, setAccountEl] = React.useState<HTMLElement | null>(null);
  const appBarRef = React.useRef<HTMLElement | null>(null);

  React.useLayoutEffect(() => {
    const appBar = appBarRef.current;
    if (!appBar) return;
    const updateNavbarHeight = () => {
      document.documentElement.style.setProperty('--kundlistar-navbar-height', `${Math.ceil(appBar.getBoundingClientRect().height)}px`);
    };
    updateNavbarHeight();
    const observer = new ResizeObserver(updateNavbarHeight);
    observer.observe(appBar);
    window.addEventListener('resize', updateNavbarHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateNavbarHeight);
    };
  }, []);

  const closeMenus = () => {
    setMenuEl(null);
    setAccountEl(null);
  };

  const handleLogout = () => {
    logout();
    closeMenus();
    navigate('/');
  };

  const publicItems = [
    { label: 'Calculate', path: '/calculate', icon: <Calculate /> },
    { label: 'Studio', path: '/studio', icon: <ShowChart /> },
    { label: 'Features', path: '/studio/features', icon: <ShowChart /> },
    { label: 'Systems', path: '/studio/systems', icon: <Calculate /> },
    { label: 'Pricing', path: '/studio/pricing', icon: <MenuBook /> },
    { label: 'Blog', path: '/blog', icon: <MenuBook /> },
  ];

  const appItems = [
    { label: 'Dashboard', path: '/dashboard', icon: <ShowChart /> },
    { label: 'Create Chart', path: '/calculate', icon: <Calculate /> },
    { label: 'Clients', path: '/cms/clients', icon: <PeopleAlt /> },
    { label: 'Planner', path: '/planner', icon: <Class /> },
    { label: 'Reports', path: '/cms/reports', icon: <MenuBook /> },
    { label: 'Almanac', path: '/panchang', icon: <Calculate /> },
    { label: 'Muhurat', path: '/muhurat', icon: <AccessTime /> },
    { label: 'Birth Mahurat', path: '/birth-muhurat', icon: <AccessTime /> },
    { label: 'Tajika Varshphal', path: '/varshphal', icon: <WbSunny /> },
    { label: 'Vedic Numerology', path: '/vedic-numerology', icon: <Calculate /> },
    { label: 'Watch Recommendation', path: '/watch-recommendation', icon: <AccessTime /> },
    { label: 'Practice Charts', path: '/practice-charts', icon: <Storage /> },
    { label: 'Advanced Tools', path: '/advanced-tools', icon: <ShowChart /> },
    { label: 'Blog', path: '/blog', icon: <MenuBook /> },
  ];
  const isAdminUser = state.user?.role === 'admin' || state.user?.role === 'ADMIN';

  const navItems = state.isAuthenticated
    ? [...appItems, ...(isAdminUser ? [{ label: 'Admin', path: '/admin', icon: <AdminPanelSettings /> }] : [])]
    : publicItems;
  const isActive = (path: string) => location.pathname === path;
  const trackAppStoreClick = () => {
    const browserWindow = window as Window & { dataLayer?: Array<Record<string, unknown>> };
    browserWindow.dataLayer = browserWindow.dataLayer || [];
    browserWindow.dataLayer.push({
      event: 'app_store_click',
      app: 'kundlistar_studio',
      placement: 'website_header',
    });
  };

  const renderNavButton = (item: typeof navItems[number]) => (
    <Button
      key={item.path}
      component={Link}
      to={item.path}
      color="inherit"
      startIcon={item.icon}
      sx={{
        borderRadius: 1,
        color: isActive(item.path) ? 'primary.dark' : 'text.secondary',
        bgcolor: isActive(item.path) ? 'primary.light' : 'transparent',
        flexShrink: 0,
        fontWeight: 600,
        minHeight: 32,
        py: 0.5,
        whiteSpace: 'nowrap',
        '&:hover': {
          bgcolor: isActive(item.path) ? 'primary.light' : 'action.hover',
        },
      }}
    >
      {translatePublicSite(language, translateInline(language, item.label))}
    </Button>
  );

  return (
    <AppBar
      ref={appBarRef}
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'rgba(15, 118, 88, 0.18)',
        boxShadow: '0 5px 16px rgba(15, 77, 60, 0.14)',
        zIndex: (theme) => theme.zIndex.drawer + 20,
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 64, md: 72 },
          gap: { xs: 0.5, sm: 1, md: 2 },
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { md: 0.5 },
        }}
      >
        <Box
          component={Link}
          to={state.isAuthenticated ? '/dashboard' : '/studio'}
          sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 1, color: 'text.primary', textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          <Box
            component="img"
            src="/logo.jpg"
            alt="Kundlistar Studio"
            sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover', bgcolor: '#fff' }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              lineHeight: 1.05,
              display: 'flex',
              flexDirection: 'column',
              fontSize: { xs: 17, md: 20 },
            }}
          >
            <Box component="span">Kundlistar</Box>
            <Box component="span">Studio</Box>
          </Typography>
        </Box>

        {!isCollapsedNav && (
          <Box
            sx={{
              display: 'flex',
              alignContent: 'center',
              alignItems: 'center',
              columnGap: 0.5,
              flex: 1,
              flexWrap: 'wrap',
              minWidth: 0,
              overflow: 'hidden',
              rowGap: 0.25,
            }}
          >
            {navItems.map(renderNavButton)}
          </Box>
        )}

        <Box sx={{ flex: isCollapsedNav ? 1 : 0 }} />

        <Button
          component="a"
          href={BRAND.appStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackAppStoreClick}
          aria-label="Install Kundlistar Studio from the Apple App Store"
          variant="contained"
          startIcon={<Apple />}
          sx={{
            display: 'inline-flex',
            minWidth: { xs: 42, sm: 'auto' },
            width: { xs: 42, sm: 'auto' },
            height: 42,
            px: { xs: 0, sm: 1.75 },
            bgcolor: '#000',
            color: '#fff',
            borderRadius: 2,
            fontWeight: 800,
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': {
              m: { xs: 0, sm: '0 8px 0 -4px' },
            },
            '&:hover': { bgcolor: '#252525' },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {translatePublicSite(language, 'Install App')}
          </Box>
        </Button>

        <LanguageSwitcher compact={isMobile} />

        {isCollapsedNav && (
          <IconButton color="inherit" onClick={(event) => setMenuEl(event.currentTarget)} aria-label="menu">
            <MenuIcon />
          </IconButton>
        )}

        {state.isAuthenticated ? (
          <IconButton color="inherit" onClick={(event) => setAccountEl(event.currentTarget)} aria-label="account">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 600 }}>
              {(state.user?.name || 'S').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
        ) : !isMobile && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button component={Link} to="/login" variant="outlined">
              {translatePublicSite(language, 'Sign In')}
            </Button>
            <Button component={Link} to="/register" variant="contained" color="secondary">
              {translatePublicSite(language, 'Start Free')}
            </Button>
          </Box>
        )}
      </Toolbar>

      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={closeMenus}>
        {navItems.map((item) => (
          <MenuItem key={item.path} onClick={() => { navigate(item.path); closeMenus(); }} selected={isActive(item.path)}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>{item.icon}{translatePublicSite(language, translateInline(language, item.label))}</Box>
          </MenuItem>
        ))}
        {!state.isAuthenticated && [
          <MenuItem key="login" onClick={() => { navigate('/login'); closeMenus(); }}>{translatePublicSite(language, 'Sign In')}</MenuItem>,
          <MenuItem key="register" onClick={() => { navigate('/register'); closeMenus(); }}>{translatePublicSite(language, 'Start Free')}</MenuItem>,
        ]}
      </Menu>

      <Menu anchorEl={accountEl} open={Boolean(accountEl)} onClose={closeMenus}>
        <MenuItem onClick={() => { navigate('/profile'); closeMenus(); }}>
          <AccountCircle sx={{ mr: 1 }} /> {translatePublicSite(language, 'Profile')}
        </MenuItem>
        <MenuItem onClick={() => { navigate('/settings'); closeMenus(); }}>
          <SettingsIcon sx={{ mr: 1 }} /> {translatePublicSite(language, 'Settings')}
        </MenuItem>
        {isAdminUser && (
          <MenuItem onClick={() => { navigate('/admin'); closeMenus(); }}>
            <AdminPanelSettings sx={{ mr: 1 }} /> {translatePublicSite(language, 'Admin')}
          </MenuItem>
        )}
        {isAdminUser && (
          <MenuItem onClick={() => { navigate('/admin/blog'); closeMenus(); }}>
            <MenuBook sx={{ mr: 1 }} /> {translatePublicSite(language, 'Blog Admin')}
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <Logout sx={{ mr: 1 }} /> {translatePublicSite(language, 'Logout')}
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default Navbar;
